import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { Layer } from 'types'
import { layersKeys } from './layersKeys'
import { buildErrorMessage } from 'utils'
import { LayerMergedEvent } from 'components/ActivityEvent/Types'
import { CloseReviewActionConfig } from 'stores/Workflows/Types'
import { useRootStore } from 'stores/RootStore'
import { useArticlePassword } from 'hooks/useArticlePassword'

export interface Data {
  merge: Pick<LayerMergedEvent, 'id' | 'data'>
}

export interface Variables {
  includedChanges?: number
  payload?: Partial<CloseReviewActionConfig>
  note?: string
  includedCommentIds?: number[]
  includedUuids?: string[]
}

interface Props {
  articleKey: string
  id: string
}

export const useMergeLayer = (
  { articleKey, id }: Props,
  options?: Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>,
) => {
  const queryClient = useQueryClient()
  const rootStore = useRootStore()
  const password = useArticlePassword(articleKey)

  return useMutation<Data, unknown, Variables>(
    layersKeys.merge(id),
    async ({ includedChanges = 0, note, includedUuids, includedCommentIds } = {}) => {
      const response = await sendToApi<{ merge: Pick<LayerMergedEvent, 'id' | 'data'> }>(
        `/articles/${articleKey}/layers/${id}/merges`,
        {
          method: 'POST',
          body: {
            note,
            included_changes: includedChanges,
            delete_layer: true,
            included_uuids: includedUuids,
            included_comment_ids: includedCommentIds,
          },
        },
      )
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        options?.onSuccess?.(data, variables, context)
        if (rootStore.docStore?.lastActiveLayerId === id) {
          rootStore.docStore?.setLastActiveLayerId(undefined)
        }
        queryClient.setQueryData<{ layers: Layer[] } | undefined>(layersKeys.list(articleKey, { password }), (data) => {
          if (!data) {
            return
          }
          const { layers: oldLayers } = data
          return {
            layers: oldLayers.filter((layer: Layer) => layer.id !== id),
          }
        })
      },
    },
  )
}
